import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import Span from '@inspire/ui/chakra/Span';

import { ProfilePageWrapper } from 'components/layout/ProfilPageWrapper';
import Layout from 'components/layout/Layout';
import StandaloneDate from '@inspire/ui/chakra/forms/StandaloneDate';
import StandaloneInput from '@inspire/ui/chakra/forms/StandaloneInput';
import StandaloneSelect from '@inspire/ui/chakra/forms/StandaloneSelect';
import {
  formatLyceesOptions,
  genderOptions,
  titleOptions,
  departementOptions,
  pedagogieRoleOptions,
  familleMetiersOptions,
  filiereOptions,
  familleFromFiliere,
  familleMetiersFromKey,
} from '@inspire/ui/select-options';
import { handleErrors } from 'lib/errors';
import {
  useUser,
  useMyProfile,
  useSetMyProfile,
  useResendVerificationEmail,
  useLycees,
  useClasse,
} from 'lib/hooks';
import { didntWorkToast, resentVerificationEmailToast } from 'lib/toasts';
import moment from 'moment';
import { getSession, signOut } from 'next-auth/client';
import { useState, useEffect } from 'react';
import { nameRegex } from '@inspire/data/schemas/user/profile';
import SimpleSchema from '@inspire/data/schemas/SimpleSchema';
import { useDeleteMyAccountMutation } from 'lib/generated';
import {
  getAbsolutePath,
  LOGGED_OUT_HOME_PATH,
  MON_PROFIL_PATH,
} from 'lib/paths';
import Link from 'components/general/Link';
import { useQuestionnairePath } from 'lib/hooks';
import { TypesBac } from '@inspire/data/static/static';
import { PedagogieFiliere } from '@inspire/data/static/pedagogie';
import { sansFonts } from '@inspire/ui/static';
import { InfoIcon } from '@inspire/ui/icons';
import StandaloneCheckbox from '@inspire/ui/chakra/forms/StandaloneCheckbox';
import { OnlyLoggedIn } from 'components/auth/Redirects';
import axios from 'axios';

const MonProfilPage = () => {
  const [sentEmail, setSentEmail] = useState(false);
  const [isRedirectingToUpdateEmail, setIsRedirectingToUpdateEmail] =
    useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const { isProf, isLyceen, isEclaireur } = useUser();
  const { classe } = useClasse();
  const { mutateAsync: deleteMyAccountAsync, isLoading: isDeletingMyAccount } =
    useDeleteMyAccountMutation();
  const { myProfile, refetchMyProfile } = useMyProfile();
  const { setMyProfileAsync } = useSetMyProfile();

  const [showDeleteAccountForm, setShowDeleteAccountForm] = useState(false);
  const { resendVerificationEmailAsync, isResendVerificationEmailLoading } =
    useResendVerificationEmail({
      onSuccess: async ({ resendVerificationEmail }) => {
        if (!resendVerificationEmail?.success) {
          didntWorkToast();
        } else {
          await refetchMyProfile();
          setSentEmail(true);
          resentVerificationEmailToast();
        }
      },
    });

  const { lycees, areLyceesLoading, refetchLycees } = useLycees(
    {
      departement: myProfile?.departement,
    },
    {
      enabled: !!myProfile?.departement,
    }
  );

  useEffect(() => {
    myProfile?.departement && refetchLycees();
  }, [myProfile?.departement, refetchLycees]);

  const lyceesOptions = formatLyceesOptions(lycees).concat([
    {
      label: 'Autres',
      options: [
        {
          value: 'Autres',
          label: 'Autres',
          city: '',
          isPrioritaire: '',
        },
      ],
    },
  ]);

  const questionnairePath = useQuestionnairePath();

  const filieresBacProOptions = filiereOptions(familleMetiersOptions);

  const familleFromFiliereValue = familleFromFiliere(myProfile?.filiereBacPro);

  const familleMetiersFromKeyValue = familleMetiersFromKey(
    classe?.familleMetiers
  );

  const redirectToUpdateEmail = async (event) => {
    event.preventDefault();
    setIsRedirectingToUpdateEmail(true);
    const result = await axios.post(
      getAbsolutePath('/api/auth/sso/request-token'),
      { targetUrl: MON_PROFIL_PATH }
    );
    const { ssoUrl, token } = result.data;
    window.location.href = `${ssoUrl}/user/modifier-identifiant?auth_req_jwt=${token}&redirect_uri=${MON_PROFIL_PATH}`;
  };

  return (
    myProfile && (
      <>
        <Alert status="info" mb={5}>
          <Icon as={InfoIcon} verticalAlign="middle" mr={2} boxSize={5} />
          <Span fontFamily={sansFonts} fontWeight="500" display="inline-block">
            {isProf &&
              'En modifiant vos informations personnelles, vous modifiez les informations de votre compte Article 1 utilisées sur les autres plateformes de Article 1.'}
            {!isProf &&
              'En modifiant tes informations personnelles, tu modifies les informations de ton compte Article 1 utilisées sur les autres plateformes de Article 1.'}
          </Span>
        </Alert>
        <StandaloneInput
          mb={5}
          label="Prénom"
          helperText={
            isLyceen
              ? 'Les étudiants Éclaireurs voient ton prénom lorsque tu les contacte'
              : ''
          }
          initialValue={myProfile?.firstName}
          isRequired
          schema={{ type: String, regEx: nameRegex }}
          data-test="firstName"
          onSubmit={async (firstName) => {
            try {
              await setMyProfileAsync({ firstName });
              await getSession();
              await refetchMyProfile();
            } catch ({ errors }) {
              handleErrors(errors);
            }
          }}
        />
        <StandaloneInput
          mb={5}
          label="Nom"
          initialValue={myProfile?.lastName}
          isRequired
          schema={{ type: String, regEx: nameRegex }}
          data-test="lastName"
          onSubmit={async (lastName) => {
            try {
              await setMyProfileAsync({ lastName });
              await getSession();
              await refetchMyProfile();
            } catch ({ errors }) {
              handleErrors(errors);
            }
          }}
        />
        {(isLyceen || isEclaireur) && (
          <StandaloneSelect
            instanceId="gender"
            label="Tu es..."
            isRequired
            value={genderOptions.find((o) => o.value === myProfile.gender)}
            options={genderOptions}
            mb={5}
            maxW="300px"
            onChange={async (option) => {
              try {
                await setMyProfileAsync({ gender: option.value });
                await getSession();
                await refetchMyProfile();
              } catch ({ errors }) {
                handleErrors(errors);
              }
            }}
          />
        )}
        {(isLyceen || isEclaireur) && (
          <StandaloneDate
            datePickerProps={{
              openToDate: moment()
                .subtract(isEclaireur ? 19 : 17, 'years')
                .toDate(),
            }}
            label="Date de naissance"
            isRequired
            initialDate={
              myProfile.birthDate
                ? moment(myProfile.birthDate)
                    .utcOffset(0, true)
                    .startOf('day')
                    .toDate()
                : undefined
            }
            onChange={async (birthDate) => {
              try {
                await setMyProfileAsync({ birthDate });
                await getSession();
                await refetchMyProfile();
              } catch ({ errors }) {
                handleErrors(errors);
              }
            }}
            mb={5}
          />
        )}
        {myProfile && myProfile.email && (
          <Box mb={5}>
            <StandaloneInput
              label="Adresse email"
              initialValue={myProfile.email}
              isReadOnly
              schema={{ type: String, regEx: SimpleSchema.RegEx.Email }}
              data-test="email"
              onSubmit={async (email) => {
                try {
                  await setMyProfileAsync({ email });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />
            {isRedirectingToUpdateEmail ? (
              <>Veuillez patienter...</>
            ) : (
              <Link href="#" onClick={redirectToUpdateEmail}>
                Modifier mon email
              </Link>
            )}
          </Box>
        )}
        {!myProfile?.isEmailVerified && !isLyceen && (
          <Box mb={5}>
            <Alert status="warning" mb={3}>
              <AlertIcon />
              Votre adresse email n'est pas validée. Nous vous avons envoyé un
              lien de validation par email.
            </Alert>
            <Button
              isLoading={isResendVerificationEmailLoading}
              loadingText={isResendVerificationEmailLoading && 'Envoi en cours'}
              disabled={sentEmail}
              onClick={async () => {
                try {
                  await resendVerificationEmailAsync();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            >
              Renvoyer un lien de validation
            </Button>
          </Box>
        )}
        {isProf && (
          <>
            <StandaloneSelect
              instanceId="title"
              label="Titre"
              value={titleOptions.find((o) => o.value === myProfile.title)}
              options={titleOptions}
              mb={5}
              onChange={async (option) => {
                try {
                  await setMyProfileAsync({ title: option.value });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />

            <StandaloneSelect
              instanceId="Rôle"
              label="Fonction"
              value={pedagogieRoleOptions.find(
                (o) => o.value === myProfile.fonction
              )}
              options={pedagogieRoleOptions}
              mb={5}
              onChange={async (option) => {
                try {
                  await setMyProfileAsync({ fonction: option.value });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />

            <StandaloneInput
              mb={5}
              label="Précisez votre rôle"
              initialValue={myProfile?.fonctionAutre}
              isRequired
              schema={{ type: String }}
              display={myProfile?.fonction === 'autre' ? 'block' : 'none'}
              onSubmit={async (fonctionAutre) => {
                try {
                  await setMyProfileAsync({ fonctionAutre });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />

            <Text>
              <b> Voies </b>
            </Text>
            <SimpleGrid spacing={2} textAlign="left">
              {Object.keys(PedagogieFiliere).map((o, i) => (
                <StandaloneCheckbox
                  key={i}
                  label={PedagogieFiliere[o]}
                  isChecked={myProfile[o]}
                  onChange={async () => {
                    try {
                      await setMyProfileAsync({
                        [o]: !myProfile[o],
                      });
                      await getSession();
                      await refetchMyProfile();
                    } catch ({ errors }) {
                      handleErrors(errors);
                    }
                  }}
                />
              ))}
            </SimpleGrid>
            <br />
          </>
        )}

        {isLyceen && myProfile && (
          <>
            <StandaloneSelect
              instanceId="departement"
              label="Dans quel département est ton lycée ?"
              value={departementOptions.find(
                (o) => o.value === myProfile.departement
              )}
              options={departementOptions}
              mb={5}
              onChange={async (option) => {
                try {
                  await setMyProfileAsync({ departement: option.value });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />
            <StandaloneSelect
              instanceId="lyceeId"
              label="Dans quel lycée es-tu ?"
              isLoading={areLyceesLoading}
              value={lyceesOptions
                .map((l) => l.options)
                .flat()
                .find((o) => o.value === myProfile?.lyceeId)}
              options={lyceesOptions}
              mb={5}
              onChange={async (option) => {
                try {
                  await setMyProfileAsync({
                    lyceeId: option.value,
                    lycee: option.label,
                  });
                  await getSession();
                  await refetchMyProfile();
                } catch ({ errors }) {
                  handleErrors(errors);
                }
              }}
            />

            {myProfile?.lyceeId === 'Autres' && (
              <>
                <StandaloneInput
                  mb={5}
                  label="Nom du lycée"
                  initialValue={myProfile?.lycee}
                  isRequired
                  schema={{ type: String }}
                  onSubmit={async (lycee) => {
                    try {
                      await setMyProfileAsync({ lycee });
                      await getSession();
                      await refetchMyProfile();
                    } catch ({ errors }) {
                      handleErrors(errors);
                    }
                  }}
                />
                <StandaloneInput
                  mb={5}
                  label="Ville du lycée"
                  initialValue={myProfile?.lyceeCity}
                  isRequired
                  schema={{ type: String }}
                  onSubmit={async (lyceeCity) => {
                    try {
                      await setMyProfileAsync({ lyceeCity });
                      await getSession();
                      await refetchMyProfile();
                    } catch ({ errors }) {
                      handleErrors(errors);
                    }
                  }}
                />
              </>
            )}
            {myProfile?.niveau && (
              <FormControl id="niveau">
                <FormLabel>Niveau :</FormLabel>
                <Text id="niveau">{myProfile?.niveau}</Text>
                {!classe && (
                  <FormHelperText mb={5}>
                    Pour modifier ton niveau tu dois refaire{' '}
                    <Link href={questionnairePath}>le questionnaire</Link>.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {myProfile?.filiere && (
              <FormControl id="filiere">
                <FormLabel>Voie :</FormLabel>
                <Text id="filiere">{TypesBac[myProfile?.filiere]}</Text>
                {!classe && (
                  <FormHelperText mb={5}>
                    Pour modifier ta voie tu dois refaire{' '}
                    <Link href={questionnairePath}>le questionnaire</Link>.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {(myProfile?.filiereBacPro || classe?.familleMetiers) && (
              <FormControl id="familleMetiers">
                <FormLabel>Famille de métiers :</FormLabel>
                <Text id="familleMetiers">
                  {myProfile?.filiereBacPro
                    ? familleFromFiliereValue
                    : familleMetiersFromKeyValue}
                </Text>
                {!classe && (
                  <FormHelperText mb={5}>
                    Pour modifier ta famille de métier tu dois refaire{' '}
                    <Link href={questionnairePath}>le questionnaire</Link>.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {myProfile?.filiereBacPro && (
              <FormControl>
                <FormLabel>Spécialité de Bac professionnel :</FormLabel>
                <Text>
                  {
                    filieresBacProOptions
                      .flat()
                      .find((o) => o.value === myProfile?.filiereBacPro)?.label
                  }
                </Text>
                {!classe && (
                  <FormHelperText mb={5}>
                    Pour modifier ta spécialité tu dois refaire{' '}
                    <Link href={questionnairePath}>le questionnaire</Link>.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {classe && (
              <Text>
                <b>Année scolaire</b> : {classe?.anneeScolaire}
              </Text>
            )}
          </>
        )}

        <Button
          variant="outline"
          colorScheme="red"
          size="xs"
          onClick={() => setShowDeleteAccountForm(true)}
        >
          Supprimer mon compte
        </Button>
        <Modal
          isOpen={showDeleteAccountForm}
          onClose={() => setShowDeleteAccountForm(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                {isProf ? 'Êtes-vous' : 'Es-tu'} sûr de vouloir supprimer{' '}
                {isProf ? 'votre' : 'ton'} compte ? Cette action est
                irréversible et entraînera la perte de toutes{' '}
                {isProf ? 'vos' : 'tes'} données.
              </Text>
              <Text color="red"></Text>
              <FormControl mt={4} isRequired>
                <FormLabel>
                  {isProf ? 'Tapez votre' : 'Tape ton'} adresse email ci-dessous
                  pour confirmer la suppression de {isProf ? 'votre' : 'ton'}{' '}
                  compte.
                </FormLabel>
                <Input
                  placeholder="Adresse email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                />
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="outline"
                color="red"
                isDisabled={confirmEmail !== myProfile.email}
                mr={3}
                onClick={async () => {
                  try {
                    const success = await deleteMyAccountAsync(null);
                    if (success) {
                      signOut({
                        callbackUrl: getAbsolutePath(LOGGED_OUT_HOME_PATH),
                      });
                    }
                  } catch ({ errors }) {
                    handleErrors(errors);
                  }
                }}
                isLoading={isDeletingMyAccount}
              >
                Supprimer mon compte
              </Button>
              <Button onClick={() => setShowDeleteAccountForm(false)}>
                Annuler
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    )
  );
};

MonProfilPage.getWrappers = (page) => (
  <Layout variant="adaptative" rowVariant="gray" containerWidth="lg">
    <OnlyLoggedIn>
      <ProfilePageWrapper>{page}</ProfilePageWrapper>
    </OnlyLoggedIn>
  </Layout>
);

export default MonProfilPage;
